import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";

const SIDEBAR_SCROLL_KEY = "adminSidebarScrollTop";
const MASTER_JUST_OPENED_KEY = "adminMasterJustOpened";

const Sidebar = ({ status }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPayoutDropdownOpen, setIsPayoutDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const masterItemRef = useRef(null);
  let activeStyle = "active";

  const masterRoutes = [
    "/cuisine-list",
    "/dish-categories",
    "/ingrident",
    "/meal-type",
    "/alergies",
    "/manage-pincodes",
    "/variations",
  ];

  const isMasterActive = masterRoutes.some((path) =>
    location.pathname.startsWith(path)
  );
  const isPayoutActive = location.pathname.startsWith("/payout-management");

  const getSidebarEl = () => document.getElementById("sidebar");

  const saveSidebarScroll = (value) => {
    const sidebar = getSidebarEl();
    const scrollTop = value ?? sidebar?.scrollTop ?? 0;
    sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(scrollTop));
  };

  const restoreSidebarScroll = () => {
    const sidebar = getSidebarEl();
    const saved = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (!sidebar || saved == null) return;
    sidebar.scrollTop = Number(saved) || 0;
  };

  const scrollMasterIntoView = () => {
    const sidebar = getSidebarEl();
    const masterItem = masterItemRef.current;
    if (!sidebar || !masterItem) return;

    requestAnimationFrame(() => {
      const targetTop = Math.max(masterItem.offsetTop - 8, 0);
      sidebar.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });
      saveSidebarScroll(targetTop);
    });
  };

  useEffect(() => {
    setIsDropdownOpen(isMasterActive);
    setIsPayoutDropdownOpen(isPayoutActive);
  }, [isMasterActive, isPayoutActive]);

  // After Master click -> /cuisine-list, scroll once. Inner option clicks only restore scroll.
  useEffect(() => {
    if (!isMasterActive) return;

    const justOpened = sessionStorage.getItem(MASTER_JUST_OPENED_KEY) === "1";
    if (justOpened) {
      sessionStorage.removeItem(MASTER_JUST_OPENED_KEY);
      const timer = setTimeout(scrollMasterIntoView, 80);
      return () => clearTimeout(timer);
    }

    const timers = [
      setTimeout(restoreSidebarScroll, 0),
      setTimeout(restoreSidebarScroll, 50),
      setTimeout(restoreSidebarScroll, 150),
    ];
    return () => timers.forEach(clearTimeout);
  }, [location.pathname, isMasterActive]);

  const handleMasterMenuClick = () => {
    sessionStorage.setItem(MASTER_JUST_OPENED_KEY, "1");
    setIsDropdownOpen(true);
    navigate("/cuisine-list");
  };

  const handleMasterChildClick = (e) => {
    e.stopPropagation();
    saveSidebarScroll();
  };

  const handlePayoutMenuClick = () => {
    setIsPayoutDropdownOpen(true);
    navigate("/payout-management/scheduled-payouts");
  };
  return (
    <>
      <nav className="sidebar sidebar-offcanvas" id="sidebar">
        <ul className="nav w-100  mt-5">
          <NavLink to="/dashboard" style={{ textDecoration: "none" , marginTop: "10px" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : ""}`}>
                <a className="nav-link">
                  <span className="menu-title">Dashboard</span> <i className="mdi mdi-calendar-text menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/customer-management" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link ">
                  <span className="menu-title">Customer Management</span> <i className="mdi mdi-account-multiple menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/menu-and-restaurant-management" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Menu and Restaurant Management</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/dish-approval" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Pending Dish Approval</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/driver-managment" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Driver Management</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/order-management" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Order Management</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/transaction-management" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Transaction Management</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <li
            className={`nav-item ${isPayoutActive ? activeStyle : ""}`}
            onClick={handlePayoutMenuClick}
            style={{ cursor: "pointer" }}
          >
            <a className="nav-link w-100">
              <span className="menu-title w-100">Payout Management</span>
              <i className="mdi mdi-speedometer menu-icon" />
            </a>
          </li>
          {isPayoutDropdownOpen && (
            <li className="nav-submenu">
              <ul className="sidebar-submenu list-unstyled p-0 m-0">
                <li>
                  <NavLink
                    to="/payout-management/scheduled-payouts"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    - Scheduled Payouts
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/payout-management/admin-wallet"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    - Admin Wallet
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/payout-management/credit-debit"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    - Payout Credit &amp; Debit
                  </NavLink>
                </li>
              </ul>
            </li>
          )}
          <NavLink to="/sales-and-performance-report" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Sales And Performance Report</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/content-management-system" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Content Management System</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/global-parameter" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Global Parameter</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/review-and-rating-management" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Review & Rating</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/banner-management" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Banner Management</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <NavLink to="/notification" style={{ textDecoration: "none" }}>
            {({ isActive }) => (
              <li className={`nav-item ${isActive ? activeStyle : undefined}`}>
                <a className="nav-link w-100">
                  <span className="menu-title w-100">Notification</span> <i className="mdi mdi-speedometer menu-icon" />
                </a>
              </li>
            )}
          </NavLink>
          <li
            ref={masterItemRef}
            className={`nav-item ${isMasterActive ? activeStyle : ""}`}
            onClick={handleMasterMenuClick}
            style={{ cursor: "pointer" }}
          >
            <a className="nav-link w-100">
              <span className="menu-title w-100">Master Management</span>
              <i className="mdi mdi-speedometer menu-icon" />
            </a>
          </li>
          {isDropdownOpen && (
            <li className="nav-submenu">
              <ul className="sidebar-submenu list-unstyled p-0 m-0">
                <li>
                  <NavLink
                    to="/cuisine-list"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleMasterChildClick}
                  >
                    - Cuisine List
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/dish-categories"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleMasterChildClick}
                  >
                    - Dish Categories
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/ingrident"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleMasterChildClick}
                  >
                    - Ingredients
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/meal-type"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleMasterChildClick}
                  >
                    - Meal Types
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/alergies"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleMasterChildClick}
                  >
                    - Allergies
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/manage-pincodes"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleMasterChildClick}
                  >
                    - Manage Pincode
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/variations"
                    className={({ isActive }) =>
                      `sidebar-submenu-link ${isActive ? "active" : ""}`
                    }
                    onClick={handleMasterChildClick}
                  >
                    - Variations
                  </NavLink>
                </li>
              </ul>
            </li>
          )}
        </ul>
      </nav>
    </>
  );
};

export default Sidebar;
